import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { api, fmtAone } from "@/lib/api";

const ALLOC = [
    { key: "Mining Rewards", pct: 60, tone: "#2563EB", note: "Emitted to miners against network hashrate share. 6 billion AONE pool released over 12 years." },
    { key: "Team",           pct: 10, tone: "#3B82F6", note: "12-month cliff, 36-month linear vest. Aligned long-term." },
    { key: "Ecosystem",      pct: 10, tone: "#60A5FA", note: "Grid partnerships, industrial pilots, developer grants." },
    { key: "Validators",     pct: 10, tone: "#93C5FD", note: "Block proposer plus delegator emission pool." },
    { key: "Public Sale",    pct: 10, tone: "#00E5FF", note: "Fair-launched at $0.0001. Open to all." },
];

const TOTAL_SUPPLY = 10_000_000_000;

export default function TokenomicsPage() {
    const [t, setT] = useState(null);
    const [unlock, setUnlock] = useState(null);
    const [price, setPrice] = useState(null);

    useEffect(() => {
        api.get("/tokenomics").then((r) => setT(r.data)).catch(() => {});
        api.get("/network/unlock-schedule").then((r) => setUnlock(r.data)).catch(() => {});
        const fetchPrice = () => api.get("/network/price").then((r) => setPrice(r.data)).catch(() => {});
        fetchPrice();
        const id = setInterval(fetchPrice, 30000);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="min-h-screen">
            <section className="border-b bg-hero-gradient" style={{ borderColor: "var(--border)" }}>
                <div className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-14 pb-12">
                    <div className="eyebrow mb-3">Tokenomics</div>
                    <h1 className="text-[36px] sm:text-[52px] font-bold tracking-[-0.035em] text-[var(--text)] max-w-3xl leading-[1.05]">
                        10 billion AONE. 5 million miners. Structured for the long game.
                    </h1>
                    <p className="mt-4 text-[16px] sm:text-[18px] text-[var(--text-2)] max-w-2xl leading-[1.55]">
                        Aegis One is designed for the next 12 years. The 6-billion mining pool releases on a halving schedule so it never runs dry, and per-withdrawal caps + treasury solvency thresholds protect every miner in the pool.
                    </p>
                </div>
            </section>

            <section className="border-b" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }}>
                <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                    <KeyNum label="Max Supply" value="10,000,000,000" unit="AONE" />
                    <KeyNum label="Launch Price" value="$0.0001" unit="per AONE" />
                    <KeyNum label="Live Price" value={price ? `$${price.price_usdt.toFixed(6)}` : "-"} unit={price ? `${price.change_pct >= 0 ? "+" : ""}${price.change_pct.toFixed(2)}%` : ""} live />
                    <KeyNum label="Target Miners" value={unlock ? unlock.target_user_count.toLocaleString() : "5,000,000"} unit="lifetime users" />
                </div>
            </section>

            {/* Distribution */}
            <section className="py-16 px-5 sm:px-8">
                <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-14 items-start">
                    <div>
                        <div className="eyebrow mb-3">Supply Distribution</div>
                        <h2 className="text-[28px] sm:text-[36px] font-bold tracking-[-0.03em] text-[var(--text)] leading-[1.1]">
                            Where every AONE goes.
                        </h2>
                        <div className="mt-8 space-y-3">
                            {ALLOC.map((a) => (
                                <div key={a.key} className="card p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="w-3 h-3 rounded" style={{ background: a.tone }} />
                                            <span className="text-[15px] font-semibold text-[var(--text)]">{a.key}</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-mono text-[15px] text-[var(--text)] tabular-nums">{a.pct}%</div>
                                            <div className="font-mono text-[11px] text-[var(--text-3)] tabular-nums">
                                                {fmtAone(TOTAL_SUPPLY * a.pct / 100)} AONE
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-[12.5px] text-[var(--text-2)] mt-2 leading-snug">{a.note}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:sticky lg:top-24 space-y-4">
                        <div className="card p-6">
                            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mb-4">
                                Distribution chart
                            </div>
                            <div className="flex items-center rounded-md overflow-hidden h-4 mb-6" style={{ background: "var(--bg-raised)" }}>
                                {ALLOC.map((a) => (
                                    <div key={a.key} style={{ width: `${a.pct}%`, background: a.tone }} title={`${a.key}: ${a.pct}%`} />
                                ))}
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                {ALLOC.map((a) => (
                                    <div key={a.key} className="text-[12.5px] flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: a.tone }} />
                                        <span className="text-[var(--text)] flex-1">{a.key}</span>
                                        <span className="font-mono text-[var(--text-2)] tabular-nums">{a.pct}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Link to="/miner" className="btn-primary w-full justify-center !py-3" data-testid="tokenomics-mine-cta">
                            Start Mining AONE <ArrowRight size={13} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Unlock schedule */}
            {unlock && (
                <section className="py-16 px-5 sm:px-8 border-t" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }} data-testid="tokenomics-unlock">
                    <div className="max-w-[1200px] mx-auto">
                        <div className="mb-10 max-w-3xl">
                            <div className="eyebrow mb-3">Emission Schedule</div>
                            <h2 className="text-[28px] sm:text-[36px] font-bold tracking-[-0.03em] text-[var(--text)] leading-[1.1]">
                                How the 6 billion mining pool unlocks.
                            </h2>
                            <p className="mt-4 text-[15px] text-[var(--text-2)] leading-[1.6] max-w-2xl">
                                Starting at <span className="font-mono text-[var(--text)]">{unlock.genesis_daily_emission_aone.toLocaleString()}</span> AONE per day, network-wide emission halves every <span className="font-mono text-[var(--text)]">{unlock.halving_days}</span> days. Miners earn a share proportional to their hashrate. Total lifetime average target: <span className="font-mono text-[var(--text)]">{Math.round(unlock.avg_lifetime_aone_per_user).toLocaleString()}</span> AONE per user across {unlock.target_user_count.toLocaleString()} lifetime miners.
                            </p>
                        </div>
                        <div className="card overflow-x-auto">
                            <table className="tbl" data-testid="unlock-schedule-table">
                                <thead>
                                    <tr>
                                        <th>Period</th>
                                        <th>Days</th>
                                        <th>Daily emission</th>
                                        <th>Period emission</th>
                                        <th>Cumulative</th>
                                        <th>% of pool</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unlock.schedule.map((p) => (
                                        <tr key={p.period}>
                                            <td className="font-medium">Year {2 * (p.period - 1) + 1} - {2 * p.period}</td>
                                            <td className="font-mono">{p.starts_day} - {p.ends_day}</td>
                                            <td className="font-mono">{p.daily_emission_aone.toLocaleString()}</td>
                                            <td className="font-mono">{p.period_emission_aone.toLocaleString()}</td>
                                            <td className="font-mono">{p.cumulative_aone.toLocaleString()}</td>
                                            <td className="font-mono">{(p.cumulative_aone / unlock.mining_pool_aone * 100).toFixed(1)}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {/* Guarantees */}
            <section className="py-16 px-5 sm:px-8">
                <div className="max-w-[1200px] mx-auto">
                    <div className="mb-10 max-w-2xl">
                        <div className="eyebrow mb-3">Guarantees</div>
                        <h2 className="text-[28px] sm:text-[36px] font-bold tracking-[-0.03em] text-[var(--text)] leading-[1.1]">
                            No overdistribution. Ever.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <Guarantee title="Hard cap 10 B" body="Total supply is capped at 10 billion AONE. No inflation. No mints beyond the schedule." />
                        <Guarantee title="Halving every 2 years" body="Global emission halves on a strict cadence. Rewards taper predictably so early miners hold more scarce tokens." />
                        <Guarantee title="Auto-pool treasury" body="USDT deposits capitalize the withdrawal pool. Reserve ratio must exceed 18% or claims pause to protect miners." />
                        <Guarantee title="Per-claim 2% cap" body="No single withdrawal can exceed 2% of the treasury. Prevents whales from draining the pool in one shot." />
                        <Guarantee title="Live market price" body="AONE-USDT rate is fed live from the market oracle. Withdrawals swap at the current rate transparently." />
                        <Guarantee title="6 B miner allocation" body="60% of supply reserved exclusively for miners. No team unlocks compete with mining rewards." />
                    </div>
                </div>
            </section>
        </div>
    );
}

function KeyNum({ label, value, unit, live }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] flex items-center gap-1.5">
                {label}
                {live && <span className="chip chip-live !py-0 !text-[9px]">LIVE</span>}
            </div>
            <div className="text-[22px] sm:text-[26px] font-bold text-[var(--text)] tabular-nums mt-1">{value}</div>
            <div className="text-[11px] font-mono text-[var(--text-3)] tabular-nums mt-0.5">{unit}</div>
        </div>
    );
}

function Guarantee({ title, body }) {
    return (
        <div className="card panel-hover p-6">
            <CheckCircle size={20} weight="fill" className="text-[var(--primary-glow)] mb-4" />
            <div className="text-[16px] font-semibold text-[var(--text)]">{title}</div>
            <p className="text-[13.5px] text-[var(--text-2)] leading-[1.55] mt-2">{body}</p>
        </div>
    );
}
